import { useState } from 'react';
import Swal from 'sweetalert2';
import type { WebFrequency } from '@/mocks/superAdminFrequencies';

const baseSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

interface FrequencyActionModalProps {
  frequency: WebFrequency | null;
  onClose: () => void;
  onAssign: (f: WebFrequency) => void;
  onReserve: (f: WebFrequency) => void;
  onBlock: (f: WebFrequency) => void;
  onViewHistory: (f: WebFrequency) => void;
  onUnblock: (f: WebFrequency) => void;
  onRelease: (f: WebFrequency) => void;
}

function statusLabel(freq: WebFrequency): string {
  switch (freq.status) {
    case 'asignada': return 'Asignada';
    case 'reservada': return 'Reservada';
    case 'bloqueada': return 'Bloqueada';
    default: return 'Libre';
  }
}

function statusColor(freq: WebFrequency): string {
  switch (freq.status) {
    case 'asignada': return 'text-primary-700 bg-primary-100';
    case 'reservada': return 'text-amber-700 bg-amber-50';
    case 'bloqueada': return 'text-red-600 bg-red-50';
    default: return 'text-emerald-700 bg-emerald-50';
  }
}

export default function FrequencyActionModal({
  frequency,
  onClose,
  onAssign,
  onReserve,
  onBlock,
  onViewHistory,
  onUnblock,
  onRelease,
}: FrequencyActionModalProps) {
  const [inlineMsg, setInlineMsg] = useState<string | null>(null);

  if (!frequency) return null;

  const showInline = (msg: string) => {
    setInlineMsg(msg);
    setTimeout(() => setInlineMsg(null), 3000);
  };

  const isLibre = frequency.status === 'libre';
  const isAsignada = frequency.status === 'asignada';
  const isReservada = frequency.status === 'reservada';
  const isBloqueada = frequency.status === 'bloqueada';

  const subtextParts = [statusLabel(frequency)];
  if (frequency.assignedRadio) subtextParts.push(frequency.assignedRadio);
  if (!frequency.assignedRadio && frequency.reservedFor) subtextParts.push(frequency.reservedFor);

  const fireAndClose = (fn: (f: WebFrequency) => void, msg: string) => {
    onClose();
    setTimeout(() => { fn(frequency); showInline(msg); }, 80);
  };

  const handleBlockConfirm = async () => {
    const result = await baseSwal.fire({
      title: '¿Bloquear frecuencia?',
      html: `
        <div style="text-align:left;font-size:12px;">
          <p>Vas a bloquear <strong style="font-family:monospace;">${frequency.frequency}</strong>.</p>
          <p style="color:#6b7280;margin-top:4px;">La frecuencia quedará inhabilitada y no podrá ser asignada.</p>
          ${frequency.assignedRadio ? `<p style="color:#ef4444;margin-top:4px;font-weight:600;">¡Atención! ${frequency.assignedRadio} perderá esta frecuencia.</p>` : ''}
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, bloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
    });

    if (result.isConfirmed) {
      onClose();
      setTimeout(() => { onBlock(frequency); showInline('Frecuencia bloqueada correctamente'); }, 80);
    }
  };

  const handleUnblockConfirm = async () => {
    const result = await baseSwal.fire({
      title: '¿Desbloquear frecuencia?',
      text: `${frequency.frequency} volverá a estar disponible.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, desbloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d1d5db',
    });

    if (result.isConfirmed) {
      onClose();
      setTimeout(() => { onUnblock(frequency); showInline('Frecuencia desbloqueada correctamente'); }, 80);
    }
  };

  const handleReleaseConfirm = async () => {
    const actionWord = isReservada ? 'cancelar la reserva' : 'liberar';
    const result = await baseSwal.fire({
      title: isReservada ? '¿Cancelar reserva?' : '¿Liberar frecuencia?',
      html: `
        <div style="text-align:left;font-size:12px;">
          <p>La frecuencia <strong style="font-family:monospace;">${frequency.frequency}</strong> será liberada.</p>
          ${frequency.assignedRadio ? `<p style="color:#ef4444;margin-top:4px;font-weight:600;">${frequency.assignedRadio} quedará sin frecuencia.</p>` : ''}
          ${frequency.reservedFor ? `<p style="color:#f59e0b;margin-top:4px;">Reserva de ${frequency.reservedFor} será cancelada.</p>` : ''}
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sí, ${actionWord}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#d1d5db',
    });

    if (result.isConfirmed) {
      onClose();
      setTimeout(() => { onRelease(frequency); showInline('Frecuencia liberada correctamente'); }, 80);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm bg-background-50 border border-background-200 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-background-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-foreground-800 text-sm font-bold font-mono">{frequency.frequency}</p>
              <p className="text-foreground-400 text-[10px] mt-0.5">{subtextParts.join(' · ')}</p>
              <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[9px] font-medium ${statusColor(frequency)}`}>
                {statusLabel(frequency)}
              </span>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors shrink-0"
            >
              <i className="ri-close-line text-foreground-500 text-sm"></i>
            </button>
          </div>
        </div>

        {/* Inline message */}
        {inlineMsg && (
          <div className="px-4 pt-3">
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-medium">
              <i className="ri-check-line text-emerald-500 text-xs"></i>
              {inlineMsg}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-2 space-y-0.5">
          {/* ─── LIBRE ─── */}
          {isLibre && (
            <>
              <button
                onClick={() => fireAndClose(onAssign, 'Frecuencia asignada correctamente')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-link text-primary-500 text-sm"></i>
                </div>
                Asignar
              </button>
              <button
                onClick={() => fireAndClose(onReserve, 'Frecuencia reservada correctamente')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-bookmark-line text-amber-500 text-sm"></i>
                </div>
                Reservar
              </button>
              <button
                onClick={handleBlockConfirm}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 text-xs hover:bg-red-50 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                  <i className="ri-forbid-line text-red-500 text-sm"></i>
                </div>
                Bloquear
              </button>

              <div className="my-1 border-t border-background-200 mx-3"></div>

              <button
                onClick={() => fireAndClose(onViewHistory, '')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-history-line text-foreground-500 text-sm"></i>
                </div>
                Historial
              </button>
            </>
          )}

          {/* ─── ASIGNADA ─── */}
          {isAsignada && (
            <>
              <button
                onClick={handleReleaseConfirm}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-amber-600 text-xs hover:bg-amber-50 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-link-unlink text-amber-500 text-sm"></i>
                </div>
                Liberar
              </button>
              <button
                onClick={handleBlockConfirm}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 text-xs hover:bg-red-50 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                  <i className="ri-forbid-line text-red-500 text-sm"></i>
                </div>
                Bloquear
              </button>

              <div className="my-1 border-t border-background-200 mx-3"></div>

              <button
                onClick={() => fireAndClose(onViewHistory, '')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-history-line text-foreground-500 text-sm"></i>
                </div>
                Historial
              </button>
            </>
          )}

          {/* ─── RESERVADA ─── */}
          {isReservada && (
            <>
              <button
                onClick={() => fireAndClose(onAssign, 'Frecuencia asignada correctamente')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-link text-primary-500 text-sm"></i>
                </div>
                Asignar
              </button>
              <button
                onClick={handleReleaseConfirm}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 text-xs hover:bg-red-50 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                  <i className="ri-close-circle-line text-red-500 text-sm"></i>
                </div>
                Liberar reserva
              </button>
              <button
                onClick={handleBlockConfirm}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 text-xs hover:bg-red-50 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                  <i className="ri-forbid-line text-red-500 text-sm"></i>
                </div>
                Bloquear
              </button>

              <div className="my-1 border-t border-background-200 mx-3"></div>

              <button
                onClick={() => fireAndClose(onViewHistory, '')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-history-line text-foreground-500 text-sm"></i>
                </div>
                Historial
              </button>
            </>
          )}

          {/* ─── BLOQUEADA ─── */}
          {isBloqueada && (
            <>
              {frequency.blockedReason && (
                <div className="px-3 py-2 mb-1 rounded-lg bg-red-50/50 text-red-600 text-[10px] flex items-start gap-1.5">
                  <i className="ri-information-line text-[11px] mt-0.5 shrink-0"></i>
                  <span>Motivo: {frequency.blockedReason}</span>
                </div>
              )}
              <button
                onClick={handleUnblockConfirm}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-emerald-600 text-xs hover:bg-emerald-50 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <i className="ri-play-circle-line text-emerald-500 text-sm"></i>
                </div>
                Desbloquear
              </button>

              <div className="my-1 border-t border-background-200 mx-3"></div>

              <button
                onClick={() => fireAndClose(onViewHistory, '')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-foreground-700 text-xs hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-history-line text-foreground-500 text-sm"></i>
                </div>
                Historial
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-background-200 text-center">
          <button
            onClick={onClose}
            className="text-foreground-400 text-[10px] font-medium hover:text-foreground-600 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}